import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logoutUser, getEntitlement, fetchOrgMetaData, fetchOrgDomains, fetchErrorMessages } from "../Api/apiService";
import { Country, State, City } from 'country-state-city';
import useEntitlementsStore from "Store/useEntitlementsStore";
import useAuthStore from "Store/useAuthStore";
import analytics from "../Utils/analytics";
import { MixpanelProvider } from "../Utils/analytics/providers/mixpanel.provider";
import { decodeToken } from "../Components/SbSessionCheckerComponent/SbSessionChecker";
import { isTokenValid } from "../Utils/tokenValidation";
import { clearClientAuthState } from "../Utils/authSession";
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USERNAME_KEY,
  NAME_KEY,
  DOMAIN_KEY,
  SUBSCRIPTION_TYPE_KEY,
  AUTH_TYPE_KEY,
  SSO_IDP_LOGIN_URL_KEY,
} from "../constants/localStorage.constants";

export const AuthContext = createContext();

const normalizeOrgDomains = (value) => {
  const rawDomains = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/[;,]+/)
      : [];

  return Array.from(
    new Set(
      rawDomains
        .map((domain) => String(domain || '').trim().toLowerCase())
        .filter(Boolean)
    )
  );
};

const extractOrgDomainsFromResponse = (response = {}) => normalizeOrgDomains(
  response?.org_domain
  ?? response?.org_domains
  ?? response?.domains
  ?? response?.data?.org_domain
  ?? response?.data?.org_domains
  ?? response?.data?.domains
);

export const AuthProvider = ({ children }) => {
  const [entitlements, setEntitlements] = useState([]);
  const [entitlementsData, setEntitlementsData] = useState([]);
  
  // Initialize user data only if token is valid
  const [email, setEmail] = useState(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    return (token && isTokenValid(token, 'AuthContext:Init')) ? localStorage.getItem(USERNAME_KEY) : null;
  });
  const [loadingEntitlements, setLoadingEntitlements] = useState(true); // Loading state
  const [initialLoad, setInitialLoad] = useState(true); // Add initial load state

  const [profileName, setProfileName] = useState(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    return (token && isTokenValid(token, 'AuthContext:Init')) ? (localStorage.getItem(NAME_KEY) || "") : "";
  });
  const [profileRole, setProfileRole] = useState("");
  const [orgDomain, setOrgDomain] = useState(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    return (token && isTokenValid(token, 'AuthContext:Init')) ? (localStorage.getItem(DOMAIN_KEY) || "") : "";
  });
  const [subscriptionType, setSubscriptionType] = useState(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    return (token && isTokenValid(token, 'AuthContext:Init')) ? (localStorage.getItem(SUBSCRIPTION_TYPE_KEY) || "") : "";
  });
  const [orgMetaData, setOrgMetaData] = useState([]);
  const [orgId, setOrgId] = useState(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!(token && isTokenValid(token, 'AuthContext:Init'))) return null;

    const decoded = decodeToken(token);
    return decoded?.org_unique_id ?? null;
  });
  const [orgDomains, setOrgDomains] = useState([]);
  const [hqCurrency, setHqCurrency] = useState('USD');
  const [loadingOrgMetaData, setLoadingOrgMetaData] = useState(true); // Loading state
  const [errorMessageData, setErrorMessageData] = useState([]);
  const [loadingErrorMessageData, setLoadingErrorMessageData] = useState(true); // Loading state

  const [accessToken, setAccessToken] = useState(() => {
    // Validate token on initialization
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token && !isTokenValid(token, 'AuthContext:Init')) {
      // Clear expired token from localStorage
      console.info("[AuthContext:Init] Clearing expired access token from localStorage");
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USERNAME_KEY);
      localStorage.removeItem(NAME_KEY);
      localStorage.removeItem(DOMAIN_KEY);
      localStorage.removeItem(SUBSCRIPTION_TYPE_KEY);
      localStorage.removeItem(AUTH_TYPE_KEY);
      localStorage.removeItem(SSO_IDP_LOGIN_URL_KEY);

      return null;
    }
    // If token is completely absent, also clean up any stale SSO data
    // so the AuthGuard SSO redirect doesn't fire with orphaned values
    if (!token) {
      localStorage.removeItem(AUTH_TYPE_KEY);
      localStorage.removeItem(SSO_IDP_LOGIN_URL_KEY);
    }
    return token;
  });
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem(REFRESH_TOKEN_KEY));

  const [loadingLocations, setloadingLocations] = useState(false);
  const [statesAndCities, setStatesAndCities] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  // Combined initializing state - stays true until all initial data is loaded
  const initializing = loadingEntitlements || loadingOrgMetaData || loadingErrorMessageData;

  const navigate = useNavigate();

  const logout = useCallback(async () => {
    // Track before clearing state — must fire before reset() wipes the identity
    analytics.trackLogout();

    try {
      if (accessToken) {
        // Check if accessToken exists before calling logoutUser
        await logoutUser(accessToken, refreshToken); // use accessToken and refreshToken
      }
    } catch (error) {
      console.error("Logout error:", error);
      // Optional: Add user notification here (e.g., a toast or alert)
    }

    clearAuthState(); // Call AFTER the API call (if any)
    navigate("/");
  }, [accessToken, refreshToken]); // Add accessToken and refreshToken as dependencies

  const fetchAndSetEntitlements = async () => {
    // Token is already validated during state initialization
    // Only check for existence here
    if (!accessToken) {
      setLoadingEntitlements(false);
      return;
    }

    setLoadingEntitlements(true);
    useEntitlementsStore.getState().setLoadingEntitlements(true);

    try {
      const entitlementsData = await getEntitlement(accessToken);
      if (entitlementsData[0]?.role) {
        setProfileRole(entitlementsData[0].role);
        useEntitlementsStore.getState().setProfileRole(entitlementsData[0].role);
      }
      setEntitlementsData(entitlementsData);
      useEntitlementsStore.getState().setEntitlementsData(entitlementsData);
      setEntitlements(entitlementsData[0]?.resources); // Set merged entitlements to state
      useEntitlementsStore.getState().setEntitlements(entitlementsData[0]?.resources ?? []);
    } catch (error) {
      console.error("Error fetching entitlements:", error);
      if (
        error.message.includes("Unauthorized") ||
        (error.response && error.response.status === 401)
      ) {
        logout(); // Call logout if unauthorized
        return; // Important: stop further execution
      }
      setEntitlements({}); // Set to empty object or handle as needed
    } finally {
      setLoadingEntitlements(false);
      useEntitlementsStore.getState().setLoadingEntitlements(false);
      // Only navigate on the initial render, when accessToken is set the first time
      if (initialLoad) {
        setInitialLoad(false); // Set initialLoad to false so it doesn't navigate again
      }
    }
  };

  const fetchAndSetOrgMetaData = async () => {
    // Token is already validated during state initialization
    // Only check for existence here
    if (!accessToken) {
      setLoadingOrgMetaData(false);
      return;
    }
    setLoadingOrgMetaData(true);
    try {
      const orgMetaDataVal = await fetchOrgMetaData(accessToken);
      if (orgMetaDataVal) {
        setOrgMetaData(orgMetaDataVal);
        useAuthStore.getState().setOrgMetaData(orgMetaDataVal);
        // Extract hq_currency from org metadata (may be nested in org_metadata_json)
        const hqCur = orgMetaDataVal?.hq_currency
          || orgMetaDataVal?.org_metadata_json?.hq_currency
          || 'USD';
        setHqCurrency(hqCur);
        useAuthStore.getState().setHqCurrency(hqCur);
      }

    } catch (error) {
      console.error("Error fetching orgMetaData:", error);
      if (
        error.message.includes("Unauthorized") ||
        (error.response && error.response.status === 401)
      ) {
        logout(); // Call logout if unauthorized
        return; // Important: stop further execution
      }
      setOrgMetaData({}); // Set to empty object or handle as needed
    } finally {
      setLoadingOrgMetaData(false);
      if (initialLoad) {
        setInitialLoad(false); // Set initialLoad to false so it doesn't navigate again
      }
    }
  };

  const fetchAndSetOrgDomains = useCallback(async (organizationId) => {
    if (!accessToken || !organizationId) {
      setOrgDomains([]);
      useAuthStore.getState().setOrgDomains([]);
      return;
    }

    try {
      const orgDomainsResponse = await fetchOrgDomains(organizationId);
      const normalizedDomains = extractOrgDomainsFromResponse(orgDomainsResponse);
      setOrgDomains(normalizedDomains);
      useAuthStore.getState().setOrgDomains(normalizedDomains);
    } catch (error) {
      console.error('Error fetching org domains:', error);
      if (
        error.message.includes('Unauthorized') ||
        (error.response && error.response.status === 401)
      ) {
        logout();
        return;
      }
      setOrgDomains([]);
      useAuthStore.getState().setOrgDomains([]);
    }
  }, [accessToken, logout]);


  const fetchAndSetErrorMessageData = async () => {

    setLoadingErrorMessageData(true);
    try {
      const errorMessageDataVal = await fetchErrorMessages();
      if (errorMessageDataVal) {
        setErrorMessageData(errorMessageDataVal);
      }

    } catch (error) {
      console.error("Error fetching error messages:", error);
      if (
        error.message.includes("Unauthorized") ||
        (error.response && error.response.status === 401)
      ) {
        logout(); // Call logout if unauthorized
        return; // Important: stop further execution
      }
      setErrorMessageData({}); // Set to empty object or handle as needed
    } finally {
      setLoadingErrorMessageData(false);
      if (initialLoad) {
        setInitialLoad(false); // Set initialLoad to false so it doesn't navigate again
      }
    }
  };


  useEffect(() => {
    setloadingLocations(true);
    const fetchData = async () => {
      try {
        // Fetch all countries
        const countries = await Country.getAllCountries();
        setCountryOptions(countries);
        const cityCountryArray = [];  // Array to store city-country combinations

        for (let i = 0; i < countries.length; i++) {
          const country = countries[i];
          const states = await State.getStatesOfCountry(country.isoCode);

          for (const state of states) {
            const cities = await City.getCitiesOfState(country.isoCode, state.isoCode);

            cities.forEach((city) => {
              // Combine city, state and country in the required format
              const cityStateCountryCombination = `${city.name} - ${state.name}, ${country.name}`;


              // Add this combination to the array
              cityCountryArray.push({
                name: cityStateCountryCombination,
                type: 'city - state - country',
                country: country.name,
                state: state.name,
                city: city.name,
              });
            });
          }

          await new Promise(resolve => setTimeout(resolve, 0)); // Break to update UI
        }

        setStatesAndCities(cityCountryArray);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setloadingLocations(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchAndSetEntitlements();
    fetchAndSetOrgMetaData();
    fetchAndSetErrorMessageData();
  }, [accessToken, initialLoad]);

  const clearAuthState = () => {
    setEmail(null);
    setAccessToken(null);
    setRefreshToken(null);
    setOrgId(null);
    setOrgDomains([]);
    clearClientAuthState();
  };

  useEffect(() => {
    if (accessToken) {
      const decoded = decodeToken(accessToken);
      const decodedOrgId = decoded?.org_unique_id ?? null;
      setOrgId(decodedOrgId);
      useAuthStore.getState().setOrgId(decodedOrgId);
      MixpanelProvider.setOrgUniqueId(decoded?.org_unique_id ?? null);
    } else {
      setOrgId(null);
      setOrgDomains([]);
      useAuthStore.getState().setOrgId(null);
      useAuthStore.getState().setOrgDomains([]);
      MixpanelProvider.setOrgUniqueId(null);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchAndSetOrgDomains(orgId);
  }, [orgId, fetchAndSetOrgDomains]);

  useEffect(()=>{
    if(email && profileName ){
      analytics.identifyUser({ email, name: profileName, role: profileRole });
    }
  },[email,profileName,profileRole])

  // ── Sync to useAuthStore (V2 components read from store instead of context) ──
  useEffect(() => { useAuthStore.getState().setEmail(email); }, [email]);
  useEffect(() => { useAuthStore.getState().setProfileName(profileName); }, [profileName]);
  useEffect(() => { useAuthStore.getState().setAccessToken(accessToken); }, [accessToken]);
  useEffect(() => { useAuthStore.getState().setRefreshToken(refreshToken); }, [refreshToken]);
  useEffect(() => { useAuthStore.getState().setOrgId(orgId); }, [orgId]);
  useEffect(() => { useAuthStore.getState().setOrgDomains(orgDomains); }, [orgDomains]);
  return (
    <AuthContext.Provider
      value={{
        email,
        setEmail,
        accessToken,
        setAccessToken,
        refreshToken,
        setRefreshToken,
        logout,
        entitlements,
        setEntitlements,
        loadingEntitlements,
        initializing,
        entitlementsData,
        profileName,
        setProfileName,
        profileRole,
        setProfileRole,
        orgId,
        orgDomains,
        orgMetaData,
        hqCurrency,
        loadingOrgMetaData,
        orgDomain,
        setOrgDomain,
        subscriptionType,
        setSubscriptionType,
        errorMessageData,
        loadingErrorMessageData,
        loadingLocations,
        statesAndCities,
        setStatesAndCities,
        countryOptions,
        setCountryOptions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
